import React, { useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGet } from './hooks/useGet'
import { CollectionCards } from './shared/CollectionCards'
import { RadAppLayout } from './common/RadAppLayout'
import { RadBox } from './common/RadBox'
import { RadContainer } from './common/RadContainer'
import { RadExpandableSection } from './common/RadExpandableSection'
import { RadGrid } from './common/RadGrid'
import { RadHeader } from './common/RadHeader'
import { RadInput2 } from './common/RadInput2'
import { RadLink } from './common/RadLink'
import { RadButton } from './common/RadButton'
import { RadSpaceBetween } from './common/RadSpaceBetween'
import { AppContext } from './App'
import { RadIcon } from './common/RadIcon'
import { RadPopover } from './common/RadPopover'
import './Dashboard.scss'

export function Dashboard () {
  const navigate = useNavigate()
  const [searchText, setSearchText] = useState()
  const { setNavigationOpen } = useContext(AppContext)
  const { data: featuredCollections } = useGet('/api/collection/featured')

  return (
    <div className='dashboard'>
      <RadAppLayout
        breadcrumbs=''
        disableContentPaddings
        content={
          <RadSpaceBetween size='l' className='container'>
            <RadSpaceBetween size='l' className='search-browse'>
              <RadSpaceBetween size='xs' className='search'>
                <h1>Search the DASH Knowledge Base</h1>
                <div className='search-container'>
                  <RadInput2
                    initialValue={searchText}
                    placeholder='Type your search terms here and hit Enter'
                    type='search'
                    onEnter={({ detail }) => {
                      setSearchText(detail.value)
                      if (detail.value === '') { return }
                      setNavigationOpen(false)
                      navigate(`/search?search=${detail.value.replaceAll(' ', '+')}`)
                    }}
                  />
                </div>
                <div className='hint'>
                  Do not use quotes or special characters.
                  <RadPopover
                    dismissButton={false}
                    position='top'
                    size='medium'
                    triggerType='text-inline'
                    content={
                      <div>
                        <p>The DKB engine returns and orders results based on a sophisticated combination of exact matches and content that's related in meaning to your search words.</p>
                        <p>There is no need to further specify your search by using special characters, such as quotation marks, dashes, or asterisks.</p>
                      </div>
                    }
                  >
                    <RadIcon name='status-info' />
                  </RadPopover>
                </div>
              </RadSpaceBetween>

              <RadSpaceBetween size='l' className='browse'>
                <span>Or Browse By Category:</span>
                <RadSpaceBetween size='xs' className='browse-buttons'>
                  <RadSpaceBetween direction='horizontal' size='xs'>
                    <button onClick={() => { navigate('/resource') }}>Resources</button>
                    <button onClick={() => { navigate('/project') }}>Projects</button>
                  </RadSpaceBetween>
                  <button onClick={() => { navigate('/organization-collaborative') }}>
                    Organizations & Collaboratives
                  </button>
                </RadSpaceBetween>
              </RadSpaceBetween>
            </RadSpaceBetween>

            <div className='about section'>
              <div className='section-header'>
                <h2 className='description'>The DASH Knowledge Base (DKB) is a repository of tools and information for the field of multisector data-sharing for health.</h2>
              </div>
              <div className='columns'>
                <div className='column-1'>
                  <h3>About the DKB</h3>
                  <p>The DKB was developed by <a href='https://www.dashconnect.org/'>Data Across Sectors for Health (DASH)</a>, a joint initiative of the Illinois Public Health Institute and the Michigan Public Health Institute. The goal of the DKB is to provide ideas, knowledge, and connections related to data sharing to anyone seeking to build a culture of health.</p>
                  <p>You can search or browse the database using the general search bar or categories above, the curated Collections below, or the DASH Framework.</p>
                  <RadButton
                    href='http://dashconnect.org/'
                    variant='primary'
                    external
                  >
                    Learn More About DASH
                  </RadButton>
                </div>
                <div className='column-2'>
                  <h3>The DASH Framework</h3>
                  <p>The DASH Framework represents the characteristics of successful multisector data-sharing projects. On the Framework page, click on any text within the graphic to find relevant resources and projects.</p>
                  <RadButton
                    onClick={() => { navigate('/framework') }}
                    variant='primary'
                  >
                    Explore the Framework
                  </RadButton>
                </div>
              </div>
            </div>

            {featuredCollections && featuredCollections.length > 0 && (
              <div className='featured-collections-container section'>
                <div className='featured-collections'>
                  <div className='section-header'>
                    <h2>Featured Collections</h2>
                    <p>These are regularly updated and carefully curated collections of resources around specific topic areas. Browse a collection to find out more.</p>
                  </div>
                  <div className='collections-container'>
                    <CollectionCards
                      collections={featuredCollections}
                      cardsPerRow={[
                        { cards: 1 },
                        { minWidth: 640, cards: 2 },
                        { minWidth: 960, cards: 3 }
                      ]}
                      variant='container'
                      minimal
                    />
                  </div>
                  <div className='section-actions'>
                    <RadButton href='/collection' variant='primary'>
                      Explore All Collections
                    </RadButton>
                  </div>
                </div>
              </div>
            )}
          </RadSpaceBetween>
      }
      />
    </div>
  )
}

// NOTE: This does not appear to be in use now.
export function Dashboard2 () {
  const [notifications] = useState([])
  const [expandedSection, setExpandedSection] = useState()
  const { data: tagGroups } = useGet('/api/tagGroup')

  if (tagGroups != null) {
    return (
      <RadAppLayout
        breadcrumbs=''
        notifications={notifications}
        contentHeader={
          <RadHeader
            variant='h1'
          >
            DKB: DASH Knowledge Base
          </RadHeader>
        }
        content={
          <RadSpaceBetween size='l'>
            <RadContainer
              variant='stacked'
              header={
                <RadHeader
                  variant='h1'
                >
                  A resource for your multi-sector data-sharing efforts to improve community health
                </RadHeader>
              }
            >
              <RadGrid
                gridDefinition={[
                  { colspan: { default: 12, xs: 4 } },
                  { colspan: { default: 12, xs: 4 } },
                  { colspan: { default: 12, xs: 4 } }
                ]}
              >
                <RadBox textAlign='center'>
                  <RadHeader
                    description='Search across the entire site by using general keywords.'
                  >
                    <RadBox variant='h3' textAlign='center'>
                      Global Search
                    </RadBox>
                  </RadHeader>
                  <br />
                  <RadButton href='/search'>Global Search</RadButton>
                </RadBox>
                <RadBox textAlign='center'>
                  <RadHeader
                    description='Start by narrowing your search to one of five categories.'
                  >
                    <RadBox variant='h3' textAlign='center'>
                      Category Search
                    </RadBox>
                  </RadHeader>
                  <br />
                  <RadSpaceBetween size='xs'>
                    <RadButton href='/organization-collaborative'>Organizations & Collaboratives</RadButton>
                    <RadButton href='/project'>Projects</RadButton>
                    <RadButton href='/resource'>Resources</RadButton>
                  </RadSpaceBetween>
                </RadBox>
                <RadBox textAlign='center'>
                  <RadHeader
                    description='Search within the DASH Framework.'
                  >
                    <RadBox variant='h3' textAlign='center'>
                      DASH Framework
                    </RadBox>
                  </RadHeader>
                  <br />
                  <RadButton href='/framework'>DASH Framework</RadButton>
                </RadBox>
              </RadGrid>
            </RadContainer>
            <RadContainer
              variant='stacked'
              header={
                <RadHeader
                  variant='h1'
                >
                  About the DKB
                </RadHeader>
              }
            >
              <RadSpaceBetween size='l'>
                <RadExpandableSection
                  onChange={({ detail: { expanded } }) => setExpandedSection(expanded ? 1 : null)}
                  expanded={expandedSection === 1}
                  headerText='Who is Data Across Sectors for Health (DASH)?'
                >
                  <p>
                    Data Across Sectors for Health (DASH) is a national initiative that helps communities
                    and organizations in the social services, public health, and healthcare sectors share
                    data so that every person can reach their full potential for health.
                  </p>
                  <p>
                    Since 2015, DASH has provided funding, resources, and technical assistance to more
                    than 200 community projects nationwide. DASH is co-led by the Illinois Public Health
                    Institute and the Michigan Public Health Institute.
                  </p>
                  Visit <RadLink href='https://dashconnect.org' target='_blank'>dashconnect.org</RadLink> to learn more
                </RadExpandableSection>
                <RadExpandableSection
                  onChange={({ detail: { expanded } }) => setExpandedSection(expanded ? 2 : null)}
                  expanded={expandedSection === 2}
                  headerText='What is the DASH Knowledge Base (DKB)?'
                >
                  <p>
                    The DASH Knowledge Base (DKB) is a publicly available online knowledge management
                    platform that supports the field of multisector data sharing.
                  </p>
                  <p>
                    It provides a repository of organizations, collaboratives, projects, and
                    resources. The purpose of the DKB is to help reveal new opportunities for collaboration
                    and more impactful solutions for communities pursuing health equity.
                  </p>
                </RadExpandableSection>
                <RadExpandableSection
                  onChange={({ detail: { expanded } }) => setExpandedSection(expanded ? 3 : null)}
                  expanded={expandedSection === 3}
                  headerText='What is the DASH Framework?'
                >
                  <p>
                    Since 2015, Data Across Sectors for Health (DASH) has been funding community collaborations that aim to share data, or use multi-sector data, to improve community health.
                  </p>
                  <p>
                    Along the way, DASH has collected insights and learnings from their network of partners and grant recipients. One result of this work is the DASH Framework,
                    which maps out all potential considerations in multi-sector data-sharing projects. Think of it as a map for data sharing that identifies all major landmarks.
                  </p>
                  <p>
                    It is a living, conceptual guide that is updated periodically based on insights from the field including subject matter experts, DASH grantees, and partners.
                  </p>
                  <p>
                    <RadLink href='https://www.dashconnect.org/dash-overview/#dashframework'>Learn more about the DASH Framework</RadLink>
                  </p>
                </RadExpandableSection>
              </RadSpaceBetween>
            </RadContainer>

            <RadContainer>
              <RadGrid
                gridDefinition={[
                  { colspan: { default: 12, s: 3 } },
                  { colspan: { default: 12, s: 9 } }
                ]}
              >
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <img src='https://www.networkforphl.org/wp-content/uploads/2019/09/logo.png' alt='Network for Public Health Law' style={{ width: '180px', paddingRight: '20px' }} />
                </div>
              </RadGrid>
            </RadContainer>
          </RadSpaceBetween>
        }
      />
    )
  }
}
